import { useEffect, useState } from 'react';
import { Button, CurrencyFormat, Logo, Spinner } from '@/components/generic';
import { copyToClipboard, formatUnitsToAmount, shortenAddress } from '@/utils';
import { bitcoinChain, useChainStore, useMagic } from '@package/smart-wallet';
import {
  useGetAccountInfo,
  useGetTokenBalances,
} from '@package/smart-wallet/hooks';

import ChainSelector from './ChainSelector';
import User from './User';
import LanguageSwitcher from '@/components/generic/LanguageSwitcher';

type HeaderProps = {
  className?: string;
  title?: string;
  newProduct?: boolean;
  hideSidebar?: boolean;
  onToggleSidebar?: () => void;
};

export const Header = ({
  className,
  title,
  newProduct,
  hideSidebar,
  onToggleSidebar,
}: HeaderProps) => {
  const { bitcoin } = useMagic();
  const chainId = useChainStore((state) => state.chainId);
  const [hasOverflowHidden, setHasOverflowHidden] = useState(false);
  const { isLoading, tokenSymbol, tokenAmount, tokenDecimals } =
    useGetTokenBalances();
  const { smartAccountAddress } = useGetAccountInfo();

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style') {
          const htmlElement = document.documentElement;
          const isOverflowHidden =
            window.getComputedStyle(htmlElement).overflow === 'hidden';
          setHasOverflowHidden(isOverflowHidden);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <header
      className={`fixed top-0 right-0 z-20 bg-b-surface1 max-lg:!right-0 ${
        hasOverflowHidden ? 'right-[calc(0px+var(--scrollbar-width))]' : ''
      } ${
        hideSidebar
          ? 'left-0'
          : 'left-85 max-4xl:left-70 max-3xl:left-60 max-xl:left-0'
      } ${className || ''}`}
    >
      <div
        className={`flex justify-between items-center h-22 max-md:h-18 ${
          hideSidebar ? 'center max-w-full' : 'center-with-sidebar'
        } ${newProduct ? 'max-md:flex-wrap max-md:!h-auto max-md:p-3' : ''}`}
      >
        <div
          className={`mr-3 gap-3 ${
            hideSidebar ? 'flex' : 'hidden max-xl:flex'
          }`}
        >
          <span className='hidden md:flex'>
            <Logo />
          </span>
          <Button
            className='flex-col gap-[4.5px] shrink-0 before:w-4.5 before:h-[1.5px] before:rounded-full before:bg-t-secondary before:transition-colors after:w-4.5 after:h-[1.5px] after:rounded-full after:bg-t-secondary after:transition-colors hover:before:bg-t-primary hover:after:bg-t-primary'
            onClick={onToggleSidebar}
            isCircle
            isWhite
          />
        </div>
        {title && (
          <div className='mr-auto text-h4 max-lg:text-h5 max-md:hidden'>
            {title}
          </div>
        )}
        <div
          className={`flex items-center gap-3 ${
            newProduct ? 'hidden max-md:flex' : ''
          } ${hideSidebar ? 'grow max-lg:grow-0 max-lg:ml-auto' : ''}`}
        >
          <Button
            className='max-md:hidden rounded-lg !h-9.5'
            isBlack
            href='/referral'
            as='link'
          >
            EARN 20$
          </Button>
          <LanguageSwitcher />
          <ChainSelector />
          {/* Wallet Address Label */}
          {smartAccountAddress && (
            <div className='flex items-center gap-2'>
              {isLoading ? (
                <Spinner className='bg-transparent' />
              ) : (
                <CurrencyFormat
                  className='text-sm md:text-lg'
                  currency=''
                  postfix={tokenSymbol}
                  decimalAmount={
                    tokenSymbol === 'BTC' ? tokenDecimals : undefined
                  }
                  value={Number(
                    formatUnitsToAmount({
                      string: tokenAmount,
                      decimalToken: tokenDecimals,
                    }),
                  )}
                />
              )}
              <div
                className='px-3 py-1.5 bg-b-surface2 rounded-lg text-caption text-t-secondary font-mono max-md:hidden cursor-pointer'
                onClick={() =>
                  copyToClipboard(
                    chainId === bitcoinChain.id
                      ? bitcoin.address
                      : smartAccountAddress,
                  )
                }
              >
                {shortenAddress(
                  chainId === bitcoinChain.id
                    ? bitcoin.address
                    : smartAccountAddress,
                )}
              </div>
            </div>
          )}

          {/* <Button
            className='!hidden max-lg:!flex max-md:!hidden'
            isWhite
            isCircle
            onClick={() => setVisibleSearch(true)}
          >
            <Icon name='search' />
          </Button> */}
          {/* <Notifications /> */}
          {/* <Messages /> */}
          <User />
        </div>
      </div>
    </header>
  );
};
